import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, ImageUp } from 'lucide-react';
import { toast } from '../lib/toast';

/** Longest side of a chosen thumbnail (enough for the player poster). */
const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.85;
/** Images larger than this are refused before decoding. */
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

interface ThumbnailFieldProps {
  /** The thumbnail the video has now. */
  currentUrl?: string;
  /** Where the video can be read from (for picking a frame). */
  videoUrl?: string;
  /** Known length in seconds (recordings may report none). */
  duration?: number;
  disabled?: boolean;
  /** The new thumbnail (JPEG), or null to keep the current one. */
  onChange: (thumbnail: Blob | null) => void;
}

/** Draws `source` scaled to at most MAX_WIDTH wide and returns it as a JPEG. */
const toJpeg = (source: CanvasImageSource, width: number, height: number) =>
  new Promise<Blob>((resolve, reject) => {
    const scale = Math.min(1, MAX_WIDTH / width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('No canvas')); return; }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    // Throws for a video read without CORS (the picture can't be exported).
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No image'))), 'image/jpeg', JPEG_QUALITY);
  });

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * Thumbnail of a video in Edit details: keep it, pick a frame of the video, or upload an image.
 * Nothing is saved here; the dialog saves the result.
 */
const ThumbnailField: React.FC<ThumbnailFieldProps> = ({ currentUrl, videoUrl, duration, disabled, onChange }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [length, setLength] = useState(duration && Number.isFinite(duration) ? duration : 0);
  const [time, setTime] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // The preview's object URL is released when replaced or on close.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const choose = (blob: Blob) => {
    setPreview(URL.createObjectURL(blob));
    onChange(blob);
  };

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (Number.isFinite(v.duration) && v.duration > 0) setLength(v.duration);
    // A frame a little in, as with automatic thumbnails.
    const start = Math.min(1, (Number.isFinite(v.duration) ? v.duration : length) * 0.1);
    v.currentTime = start;
    setTime(start);
  };

  const seek = (value: number) => {
    setTime(value);
    if (videoRef.current) videoRef.current.currentTime = value;
  };

  const captureFrame = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setCapturing(true);
    try {
      if (v.seeking) await new Promise((r) => v.addEventListener('seeked', r, { once: true }));
      choose(await toJpeg(v, v.videoWidth, v.videoHeight));
      setPicking(false);
    } catch {
      toast.error(t('video_thumbnail.frame_failed', 'This video’s frames can’t be used. Upload an image instead.'));
    } finally {
      setCapturing(false);
    }
  };

  const onFile = async (file?: File) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > MAX_IMAGE_BYTES) {
      toast.error(t('video_thumbnail.bad_image', 'Choose a JPG, PNG or WebP image up to 20 MB.'));
      return;
    }
    try {
      const bitmap = await createImageBitmap(file);
      try {
        choose(await toJpeg(bitmap, bitmap.width, bitmap.height));
      } finally {
        bitmap.close();
      }
    } catch {
      toast.error(t('video_thumbnail.bad_image', 'Choose a JPG, PNG or WebP image up to 20 MB.'));
    }
  };

  const shown = preview || currentUrl;
  const box: React.CSSProperties = {
    width: '100%', aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', background: '#000',
    border: '1px solid var(--theme-border, #d1d5db)', display: 'block', objectFit: 'contain',
  };
  const button: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, fontSize: 13,
    border: '1px solid var(--theme-border, #d1d5db)', background: 'transparent',
    color: 'var(--text-primary, #111)', cursor: disabled ? 'default' : 'pointer',
  };

  if (picking) {
    return (
      <div>
        <video
          ref={videoRef}
          src={videoUrl}
          crossOrigin="anonymous"
          muted
          playsInline
          preload="auto"
          onLoadedMetadata={onLoadedMetadata}
          style={box}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <input
            type="range"
            min={0}
            max={Math.max(0.1, length)}
            step={0.1}
            value={time}
            onChange={(e) => seek(Number(e.target.value))}
            disabled={disabled || capturing || !length}
            aria-label={t('video_thumbnail.frame_time', 'Frame')}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary, #6b7280)' }}>
            {fmt(time)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={button} onClick={() => setPicking(false)} disabled={disabled || capturing}>
            {t('cancel', 'Cancel')}
          </button>
          <button
            type="button"
            style={{ ...button, background: 'var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)', border: 'none' }}
            onClick={() => void captureFrame()}
            disabled={disabled || capturing}
          >
            {t('video_thumbnail.use_frame', 'Use this frame')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ width: 200, maxWidth: '100%' }}>
        {shown
          ? <img src={shown} alt="" style={box} />
          : <div style={box} aria-hidden />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {videoUrl && (
          <button type="button" style={button} onClick={() => setPicking(true)} disabled={disabled}>
            <Film size={14} aria-hidden />
            {t('video_thumbnail.pick_frame', 'Pick a frame')}
          </button>
        )}
        <button type="button" style={button} onClick={() => fileRef.current?.click()} disabled={disabled}>
          <ImageUp size={14} aria-hidden />
          {t('video_thumbnail.upload', 'Upload image')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }}
        />
        {preview && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
            {t('video_thumbnail.pending', 'New thumbnail — saved with Save.')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ThumbnailField;
